/**
 * PWA update flow (GAM-25, tech-design §9): `prompt` strategy — a freshly installed
 * service worker waits instead of clobbering the running session; the player saves,
 * then reloads into the new version. The caller supplies `saveNow` (persist before
 * activating) and `notify` (surface messages through the overlay's toast).
 */
/// <reference types="vite-plugin-pwa/client" />
import { registerSW } from 'virtual:pwa-register';

export interface PwaHooks {
  /** Persist the session before the new service worker takes over. */
  readonly saveNow: () => Promise<void>;
  /** Transient notice ("offline ready"). */
  readonly notify: (message: string) => void;
  /** An update is waiting: show UI that calls `apply()` when the player is ready. */
  readonly onUpdateReady: (apply: () => Promise<void>) => void;
}

export function initPwa(hooks: PwaHooks): void {
  const updateSW = registerSW({
    onNeedRefresh() {
      hooks.onUpdateReady(async () => {
        await hooks.saveNow();
        await updateSW(true);
      });
    },
    onOfflineReady() {
      hooks.notify('Offline ready — Incrementus now plays with no connection.');
    },
  });
}

/**
 * A manual "my build looks stale/broken" escape hatch (GAM-25 settings): drop
 * the service worker and its precached assets, then reload. Saves and settings
 * live in IndexedDB, a separate store this never touches, so they survive.
 */
export async function hardReloadApp(): Promise<void> {
  if ('serviceWorker' in navigator) {
    const registrations = await navigator.serviceWorker.getRegistrations();
    await Promise.all(registrations.map((registration) => registration.unregister()));
  }
  if ('caches' in window) {
    const keys = await caches.keys();
    await Promise.all(keys.map((key) => caches.delete(key)));
  }
  window.location.reload();
}
